/**
 * @file ReorderPreview.tsx
 *
 * Floating drag preview rendered while a {@link DraggableDataTable} row is being
 * reordered. Position is fixed to the viewport and follows the pointer. Ported
 * from the design-reviewed Website Builder v2 implementation.
 */

import type { ReactNode } from 'react';
import { Icon } from '@group.one/react-gravity';
import type { IconName } from '@group.one/gravity';
import type { DragPreviewPosition } from '../hooks/use-pointer-reorder-list';
import styles from './DraggableDataTable.module.css';

interface ReorderPreviewProps {
  position: DragPreviewPosition;
  label: ReactNode;
  iconName: IconName;
}

/**
 * Renders the cursor-following preview of the row currently being dragged.
 *
 * @param props.position - The current pointer position, or `null` when idle.
 * @param props.label - The preview label (usually the row's name).
 * @param props.iconName - The drag-handle icon to echo in the preview.
 *
 * @returns The floating preview, or `null` when no drag is in progress.
 */
export function ReorderPreview({ position, label, iconName }: ReorderPreviewProps) {
  if (position === null) return null;

  return (
    <div
      className={`${styles.dragPreview} gv-flex gv-gap-sm gv-items-center gv-pos-left gv-pos-top gv-pos-fixed gv-shadow-subtle gv-border gv-radius gv-px-sm`}
      style={{ transform: `translate(${position.x + 12}px, ${position.y + 8}px)` }}
      aria-hidden
    >
      <span className="gv-text-sm gv-px-sm">
        <Icon name={iconName} size="small" />
      </span>
      <span className="gv-text-sm gv-p-sm">{label}</span>
    </div>
  );
}
